import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  alpha,
  useTheme
} from '@mui/material';
import { User } from '../../types/chatbot';

interface SubscriptionDetailsProps {
  userProfile: User;
  onOpenSubscriptionDialog: () => void;
  onOpenCancelDialog: () => void;
}

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = ({
  userProfile,
  onOpenSubscriptionDialog,
  onOpenCancelDialog
}) => {
  const theme = useTheme();
  
  if (!userProfile) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card sx={{ 
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
      }
    }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ 
          fontWeight: 600, 
          color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e', 
          mb: 3,
          display: 'flex',
          alignItems: 'center'
        }}>
          Your Subscription
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : '#f8f9fa', 
              borderRadius: 2,
              height: '100%',
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : '#e0e0e0'}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
                fontWeight: 500
              }}>
                Current Plan
              </Typography>
              <Typography variant="h6" sx={{ 
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e', 
                mb: 1,
                fontWeight: 600
              }}>
                {userProfile.subscription.plan === 'none' ? 'No Plan' : 
                  userProfile.subscription.plan.charAt(0).toUpperCase() + userProfile.subscription.plan.slice(1)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {userProfile.subscription.status === 'active' 
                  ? `Active until: ${formatDate(userProfile.subscription.endDate)}`
                  : 'Inactive'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : '#f8f9fa', 
              borderRadius: 2,
              height: '100%',
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : '#e0e0e0'}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
                fontWeight: 500
              }}>
                Support Level
              </Typography>
              <Typography variant="h6" sx={{ 
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e', 
                mb: 1,
                fontWeight: 600
              }}>
                {userProfile.subscription.plan === 'enterprise' ? 'Priority' : 'Standard'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {userProfile.subscription.plan === 'enterprise' 
                  ? '24/7 Priority Support' 
                  : 'Standard Business Hours Support'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 3, 
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : '#f8f9fa', 
              borderRadius: 2,
              height: '100%',
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : '#e0e0e0'}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
                fontWeight: 500
              }}>
                Plan Features
              </Typography>
              <Typography variant="h6" sx={{ 
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e', 
                mb: 1,
                fontWeight: 600
              }}>
                {userProfile.subscription.plan === 'enterprise' ? 'Advanced' : 'Basic'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {userProfile.subscription.plan === 'enterprise' 
                  ? 'Including all premium features' 
                  : 'Essential features included'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: 2,
          mt: 4
        }}>
          {userProfile.subscription.status !== 'active' ? (
              <Button
                variant="contained"
                onClick={onOpenSubscriptionDialog}
                sx={{ 
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#0d1642'
                  },
                  textTransform: 'none',
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem'
                }}
              >
                Subscribe Now
              </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={onOpenSubscriptionDialog}
                sx={{ 
                  borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
                  color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a237e',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : '#0d1642',
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : 'rgba(26, 35, 126, 0.04)'
                  },
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2
                }}
              >
                Upgrade Plan
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={onOpenCancelDialog}
                sx={{ 
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2
                }}
              >
                Cancel Subscription
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SubscriptionDetails; 